import { useCallback } from 'react'
import { useUrlState } from './useUrlState'
import { VodTypeCode, VodPublishStatusCode } from '../types/vod'

export interface VodFilters {
  vodTypeCode: VodTypeCode | ''
  publishStatus: VodPublishStatusCode | ''
  startDate: string
  endDate: string
  search: string
  matchId: string
  sortOrder: 'asc' | 'desc'
  page: number
}

/**
 * Hook for managing VOD highlights list filters in URL
 * Provides type-safe filter state and updater
 */
export function useVodFilters() {
  const defaultFilters: VodFilters = {
    vodTypeCode: '',
    publishStatus: '',
    startDate: '',
    endDate: '',
    search: '',
    matchId: '',
    sortOrder: 'desc',
    page: 1,
  }

  const { state, updateState } = useUrlState<VodFilters>(
    defaultFilters,
    {
      // VOD type code - only include in URL if set
      vodTypeCode: {
        serialize: (value) => (value ? value : undefined),
        deserialize: (value) => (value as VodTypeCode) || '',
      },
      // Publish status - only include in URL if set
      publishStatus: {
        serialize: (value) => (value ? value : undefined),
        deserialize: (value) => (value as VodPublishStatusCode) || '',
      },
      // Dates - only include in URL if set
      startDate: {
        serialize: (value) => (value ? value : undefined),
        deserialize: (value) => value || '',
      },
      endDate: {
        serialize: (value) => (value ? value : undefined),
        deserialize: (value) => value || '',
      },
      // Search - only include in URL if not empty
      search: {
        serialize: (value) => (value && value.trim() ? value.trim() : undefined),
        deserialize: (value) => value || '',
      },
      // Match ID - only include in URL if set
      matchId: {
        serialize: (value) => (value ? value : undefined),
        deserialize: (value) => value || '',
      },
      // Sort order - only include in URL if not default
      sortOrder: {
        serialize: (value) => (value !== 'desc' ? value : undefined),
        deserialize: (value) => (value as 'asc' | 'desc') || 'desc',
      },
      // Page - only include in URL if > 1
      page: {
        serialize: (value) => (value > 1 ? String(value) : undefined),
        deserialize: (value) => (value ? parseInt(value, 10) : 1),
      },
    }
  )

  /** Update filters and reset to page 1 */
  const updateFilterAndResetPage = useCallback(
    (updates: Partial<VodFilters>) => {
      updateState({ ...updates, page: 1 })
    },
    [updateState]
  )

  /** Change page and scroll to top */
  const handlePageChange = useCallback(
    (newPage: number) => {
      updateState({ page: newPage })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [updateState]
  )

  return {
    filters: state,
    updateFilters: updateState,
    updateFilterAndResetPage,
    handlePageChange,
  }
}
